import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Button, Stack, TextField, Grid, Card, CardContent,
  Chip, IconButton, Tooltip, CircularProgress, Alert, Tabs, Tab, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { fetchSubjects, fetchSubjectsSummary, updateSubject, clearError } from '../slices/subjectsSlice';
import { fetchStaff } from '../slices/staffSlice';
import { fetchClasses } from '../slices/classesSlice';
import SubjectDetailView from '../components/subjects/SubjectDetailView';
import SubjectEditDialog from '../components/subjects/SubjectEditDialog';
import { useToast } from '../context/ToastContext';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const SubjectsPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { subjects, loading, error, summary } = useSelector((state) => state.subjects);
  const { filtered: staffList } = useSelector((state) => state.staff);
  const { list: classList } = useSelector((state) => state.classes);
  
  const userRole = localStorage.getItem('role');
  const isStudent = userRole === 'student';

  const [viewMode, setViewMode] = useState(isStudent ? 'cards' : 'table'); // 'table' or 'cards'
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [teacherFilter, setTeacherFilter] = useState('all');

  useEffect(() => {
    dispatch(fetchSubjects());
    // Don't fetch summary or staff/classes for students
    if (!isStudent) {
      dispatch(fetchSubjectsSummary());
      dispatch(fetchStaff());
      dispatch(fetchClasses());
    }
  }, [dispatch, isStudent]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
      dispatch(clearError());
    }
  }, [error, showToast, dispatch]);

  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
  };

  const handleBackFromDetail = () => {
    setSelectedSubject(null);
    dispatch(fetchSubjects()); // Refresh list
  };

  const handleEditClick = (subject, e) => {
    if (e) e.stopPropagation();
    setEditingSubject(subject);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingSubject(null);
  };

  const handleEditSave = async (updatedData) => {
    try {
      await dispatch(updateSubject({ id: editingSubject.id, ...updatedData })).unwrap();
      showToast('تم تحديث المادة بنجاح', 'success');
      handleEditClose();
      dispatch(fetchSubjects());
      dispatch(fetchSubjectsSummary());
    } catch (err) {
      showToast('فشل تحديث المادة', 'error');
    }
  };

  // Filter subjects
  const filteredSubjects = subjects.filter(subject => {
    // For students, backend already filters by their class, so we just need search filter
    if (isStudent) {
      const matchesSearch = !searchQuery || 
        subject.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        subject.teacher_name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch && !subject.is_archived;
    }
    
    // For other roles, use full filtering
    const matchesSearch = !searchQuery || 
      subject.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subject.teacher_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subject.school_class_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesClass = classFilter === 'all' || subject.school_class === classFilter;
    const matchesTeacher = teacherFilter === 'all' || subject.teacher === teacherFilter;
    
    return matchesSearch && matchesClass && matchesTeacher && !subject.is_archived;
  });

  // Get subject color
  const getSubjectColor = (subjectName) => {
    const colors = {
      'رياضيات': '#2196F3', 'علوم': '#4CAF50', 'فيزياء': '#FF9800',
      'كيمياء': '#9C27B0', 'احياء': '#8BC34A', 'عربي': '#F44336',
      'انجليزي': '#3F51B5', 'تاريخ': '#795548', 'جغرافيا': '#607D8B',
      'تربية': '#E91E63', 'رياضة': '#FF5722', 'فنون': '#673AB7',
      'موسيقى': '#9E9E9E', 'حاسوب': '#00BCD4'
    };
    const key = Object.keys(colors).find(k => subjectName?.includes(k));
    return key ? colors[key] : '#757575';
  };

  // If subject detail view is selected
  if (selectedSubject) {
    return (
      <SubjectDetailView
        subject={selectedSubject}
        onBack={handleBackFromDetail}
      />
    );
  }

  // Table columns
  const columns = [
    { field: 'name', headerName: 'اسم المادة', flex: 1 },
    { field: 'school_class_name', headerName: 'الصف', flex: 0.8 },
    { field: 'teacher_name', headerName: 'المعلم', flex: 1 },
    { field: 'hours_per_week', headerName: 'الساعات الأسبوعية', flex: 0.8 },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Stack useFlexGap direction="row" spacing={1}>
          <Tooltip title="عرض التفاصيل">
            <IconButton
              size="small"
              onClick={() => handleSubjectClick(params.row)}
              sx={{
                color: '#0284C7',
                borderRadius: 2,
                '&:hover': { bgcolor: '#E0F2FE', color: '#0284C7' },
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!isStudent && (
            <Tooltip title="تعديل">
              <IconButton
                size="small"
                onClick={(e) => handleEditClick(params.row, e)}
                sx={{
                  color: '#D97706',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#FEF3C7', color: '#D97706' },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  // Render cards view
  const renderCardsView = () => (
    <Grid container spacing={3}>
      {filteredSubjects.map((subject, index) => {
        const color = getSubjectColor(subject.name);
        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={subject.id}>
            <Card
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(index * 0.03, 0.3), ease: 'easeOut' }}
              elevation={0}
              sx={{
                height: '100%',
                cursor: 'pointer',
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                borderRight: `4px solid ${color}`,
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(67,56,202,0.14)',
                },
              }}
              onClick={() => handleSubjectClick(subject)}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{ color, fontFamily: 'Cairo, Tajawal, sans-serif' }}
                    >
                      {subject.name}
                    </Typography>
                    {!isStudent && (
                      <Stack useFlexGap direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditClick(subject, e)}
                          sx={{
                            color: '#D97706',
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#FEF3C7', color: '#D97706' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: '#E5E7EB' }} />

                  <Stack spacing={1.25}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon fontSize="small" sx={{ color: '#6B7280' }} />
                      <Typography variant="body2" sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        {subject.school_class_name || 'غير محدد'}
                      </Typography>
                    </Box>

                    {subject.teacher_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" sx={{ color: '#6B7280' }} />
                        <Typography variant="body2" sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                          {subject.teacher_name}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon fontSize="small" sx={{ color: '#6B7280' }} />
                      <Typography variant="body2" sx={{ color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        {subject.hours_per_week || 0} ساعة/أسبوع
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  if (loading && subjects.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#F8F9FF',
        }}
      >
        <CircularProgress sx={{ color: '#4338CA' }} />
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                {isStudent ? 'موادي الدراسية' : 'المواد الدراسية'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                {isStudent ? 'عرض المواد الدراسية لصفك' : 'إدارة وعرض المواد الدراسية والمعلمين'}
              </Typography>
            </Box>
          </Stack>

          {!isStudent && (
            <Tooltip title={viewMode === 'table' ? 'عرض البطاقات' : 'عرض الجدول'}>
              <IconButton
                onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  borderRadius: 2.5,
                  width: 44,
                  height: 44,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
                }}
              >
                {viewMode === 'table' ? <GridViewIcon /> : <ViewListIcon />}
              </IconButton>
            </Tooltip>
          )}
        </MotionPaper>

        {/* Filters - Simplified for students */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={isStudent ? 12 : 4}>
              <TextField
                fullWidth
                placeholder="بحث في المواد..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#6B7280' }} />,
                  sx: {
                    borderRadius: 2.5,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    bgcolor: '#F8F9FF',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#4338CA' },
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                }}
              />
            </Grid>
            {!isStudent && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>الصف</InputLabel>
                    <Select
                      value={classFilter}
                      onChange={(e) => setClassFilter(e.target.value)}
                      label="الصف"
                      sx={{
                        borderRadius: 2.5,
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        bgcolor: '#F8F9FF',
                        '& fieldset': { borderColor: '#E5E7EB' },
                        '&:hover fieldset': { borderColor: '#4338CA' },
                        '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                      }}
                    >
                      <MenuItem value="all">جميع الصفوف</MenuItem>
                      {classList.map((cls) => (
                        <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>المعلم</InputLabel>
                    <Select
                      value={teacherFilter}
                      onChange={(e) => setTeacherFilter(e.target.value)}
                      label="المعلم"
                      sx={{
                        borderRadius: 2.5,
                        fontFamily: 'Cairo, Tajawal, sans-serif',
                        bgcolor: '#F8F9FF',
                        '& fieldset': { borderColor: '#E5E7EB' },
                        '&:hover fieldset': { borderColor: '#4338CA' },
                        '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                      }}
                    >
                      <MenuItem value="all">جميع المعلمين</MenuItem>
                      {staffList
                        .filter(staff => staff.is_teacher && !staff.is_archived)
                        .map((teacher) => (
                          <MenuItem key={teacher.id} value={teacher.id}>
                            {teacher.first_name} {teacher.last_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </MotionPaper>

      {/* Summary Stats - Hidden for students */}
      {!isStudent && summary && (
        <Grid
          container
          spacing={2}
          sx={{ mb: 3 }}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
        >
          {[
            { value: summary.summary?.total_subjects || 0, label: 'إجمالي المواد', color: '#4338CA', bg: '#EEF2FF' },
            { value: summary.summary?.active_subjects || 0, label: 'المواد النشطة', color: '#16A34A', bg: '#DCFCE7' },
            { value: summary.summary?.total_hours_per_week || 0, label: 'إجمالي الساعات', color: '#0284C7', bg: '#E0F2FE' },
            { value: filteredSubjects.length, label: 'المواد المفلترة', color: '#D97706', bg: '#FEF3C7' },
          ].map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: '#fff',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    mx: 'auto',
                    mb: 1,
                    borderRadius: 2.5,
                    bgcolor: stat.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h6" fontWeight={800} sx={{ color: stat.color, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                    {stat.value}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Student-friendly summary */}
      {isStudent && filteredSubjects.length > 0 && (
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
          }}
        >
          <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            {filteredSubjects.length} {filteredSubjects.length === 1 ? 'مادة دراسية' : 'مواد دراسية'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.85)', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
            المواد الدراسية لصفك
          </Typography>
        </MotionPaper>
      )}

      {/* Content */}
      {viewMode === 'table' ? (
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 3,
            bgcolor: '#fff',
            border: '1px solid #E5E7EB',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          }}
        >
          <DynamicDataGrid
            rows={filteredSubjects}
            columns={columns}
            loading={loading}
            pageSize={10}
          />
        </MotionPaper>
      ) : (
        renderCardsView()
      )}

        {/* Edit Dialog - Hidden for students */}
        {!isStudent && (
          <SubjectEditDialog
            open={editDialogOpen}
            subject={editingSubject}
            onClose={handleEditClose}
            onSave={handleEditSave}
            availableTeachers={staffList.filter(staff => staff.is_teacher && !staff.is_archived)}
          />
        )}
      </Box>
    </Box>
  );
};

export default SubjectsPage;
